import { useEffect, useRef } from 'react';
import type { BluefinInvalidInputError, EncryptResponse, PaymentIFrame } from '@/types/payconex';
import { useTranslation } from '@/contexts/i18n-context';
import { env } from '@/env';
import styles from './payment-iframe.module.css';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useAsyncAction } from '@/hooks/use-async-action';
import { promiseWithResolvers } from '@/util/promise-with-resolvers';

const paymentIFrameId = 'payment_iframe';
const translationBase = 'visit.payment.iFrame';

export interface AdditionalAddressProps {
  zipCode: string;
  address: string;
  state: string;
  city: string;
}

interface OnSuccessProps {
  response: EncryptResponse;
  additionalProps: AdditionalAddressProps;
}

export function usePaymentIframe({
  onFailure,
  onInvalidInput,
}: {
  onFailure: () => void;
  onInvalidInput: (error: BluefinInvalidInputError) => void;
}) {
  const paymentIFrame = useRef<PaymentIFrame | null>(null);
  const isInitialized = useRef(false);
  const encryptionPromiseRef = useRef<PromiseWithResolvers<OnSuccessProps> | null>(null);
  const onFailureRef = useRef(onFailure);
  const onInvalidInputRef = useRef(onInvalidInput);
  onFailureRef.current = onFailure;
  onInvalidInputRef.current = onInvalidInput;
  const { t } = useTranslation();

  const isMobile = useMediaQuery('--screen-md');

  useEffect(() => {
    if (isInitialized.current) {
      return;
    }

    if (!window.PaymentiFrame) {
      // eslint-disable-next-line no-console
      console.error('PaymentIFrame is not loaded');
      return;
    }

    const paymentiFrameVal = new window.PaymentiFrame({
      create: true,
      iframeId: paymentIFrameId,
      settings: {
        account: env.VITE_PAYCONEX_IFRAME_ACC_ID,
        parentId: 'iframe_container',
        cvv: 'required',
        expy: 'single_input',
        width: '100%',
        showFrame: false,
        devServer: env.VITE_PAYCONEX_IFRAME_DEV_SERVER,

        text: {
          number: {
            label: t(`${translationBase}.cardNumber.label`),
            placeholder: t(`${translationBase}.cardNumber.placeholder`),
          },
          cvv: {
            label: t(`${translationBase}.cvv.label`),
            placeholder: t(`${translationBase}.cvv.placeholder`),
          },
          expy_single: {
            label: t(`${translationBase}.expy.label`),
            placeholder: t(`${translationBase}.expy.placeholder`),
          },
        },

        css: getCssSettings(isMobile),
      },
    });

    paymentIFrame.current = paymentiFrameVal;
    isInitialized.current = true;

    return () => {
      const iframeElement = document.getElementById(paymentIFrameId);
      if (iframeElement) {
        iframeElement.remove();
      }
      isInitialized.current = false;
    };
  }, [t, isMobile]);

  const { perform: requestEncryption, loading: isProcessing } = useAsyncAction(
    async (additionalProps: AdditionalAddressProps): Promise<OnSuccessProps> => {
      if (encryptionPromiseRef.current) {
        return encryptionPromiseRef.current.promise;
      }

      const encryptionRequest = promiseWithResolvers<OnSuccessProps>();
      encryptionPromiseRef.current = encryptionRequest;

      if (!paymentIFrame.current) {
        encryptionRequest.reject(new Error('Payment processor not initialized'));
      } else {
        paymentIFrame.current.encrypt({
          failure: () => {
            onFailureRef.current();
            encryptionRequest.reject(new Error('Card encryption failed'));
          },
          invalidInput: (error) => {
            onInvalidInputRef.current(error);
            encryptionRequest.reject(error);
          },
          success: (response) => {
            encryptionRequest.resolve({ response, additionalProps });
          },
        });
      }

      return encryptionRequest.promise.finally(() => {
        encryptionPromiseRef.current = null;
      });
    },
  );

  return { isProcessing, requestEncryption };
}

export function PaymentIframe() {
  return (
    <div
      id="iframe_container"
      data-testid="payment-iframe-container"
      className={styles.iframeContainer}
    />
  );
}

const getCssSettings = (isMobile: boolean) => ({
  id_body: `
    background: #FFFFFF;
    padding: 0;
    margin: 0;
    font-family: 'Raleway', sans-serif;
    font-variant-numeric: lining-nums;
  `,

  id_form: `
    background: transparent;
    padding: 0;
    margin: 0;
  `,

  class_row: `
    margin: 0 0 24px 0;
    padding: 0;
    width: 100%;
    border: none;
    display: block;
  `,

  id_expy_row: `
    margin: ${!isMobile && '0'};
    width: ${isMobile ? '100%' : '48%'};
    ${isMobile ? '' : 'float: left;'}
  `,

  id_cvv_row: `
    margin: ${!isMobile && '0'};
    width: ${isMobile ? '100%' : '48%'};
    ${isMobile ? '' : 'float: right;'}
  `,

  class_label: `
    display: block;
    width: 100%;
    margin: 0 0 12px 0;
    padding: 0;
    font-size: 16px;
    font-weight: 600;
    color: #1F2227;
    text-align: left;
    line-height: 22px;
  `,

  class_input_box: `
    width: 100%;
    height: 48px;
    min-height: 48px;
    padding: 12px 16px;
    font-size: 16px;
    line-height: 24px;
    color: #1a1a1a;
    background: #FFFFFF;
    border: 1px solid #C4CCC8;
    border-radius: 2px;
    box-sizing: border-box;
    outline: none;
    vertical-align: middle;
  `,

  'class_input_box:hover': `
    border-color: #94a5a5;
  `,

  id_number_input: `
    width: 100%;
    height: 48px;
    min-height: 48px;
  `,

  id_expy_input: `
    width: 100%;
    height: 48px;
    min-height: 48px;
  `,

  id_cvv_input: `
    width: 100%;
    height: 48px;
    min-height: 48px;
  `,
});
